import { useEffect, useRef } from 'react'

import { subscribeFrameFullscreenExit } from '@/lib/embedding/frameFullscreen'
import { useShadowHostPortal } from './ShadowHostPortalContext'

/**
 * iframe 埋め込みページで、モーダル表示中に全画面が外から解除されたら閉じる。
 *
 * iframe 埋め込みではモーダルの間だけ iframe を全画面化している（ShadowHostPortalContext）。
 * ブラウザの Esc や「全画面を終了」で抜けられると、モーダルは iframe の小さな枠に
 * 押し込まれた状態で残る。それを避けるため、解除を検知した時点でモーダル自身を閉じる。
 *
 * - script 埋め込み（`isFrameEmbed=false`）では何もしない。
 * - `isOpen=false` の間は購読しない。閉じる操作に伴う自分自身の全画面解除で
 *   二重に onClose が走らないよう、1 回の表示につき最大 1 回だけ呼ぶ。
 * - 全画面に入れなかった場合（iPhone / allowfullscreen 無し）は解除イベントが
 *   発生しないので、モーダルは iframe 内に留まったまま通常どおり操作できる。
 *
 * @param isOpen モーダルが開いているか
 * @param onClose 全画面が解除されたときに呼ぶ閉じ処理（通常の「閉じる」と同じもの）
 */
export const useFrameFullscreenExit = (isOpen: boolean, onClose: () => void): void => {
  const isFrameEmbed = useShadowHostPortal()?.isFrameEmbed ?? false
  // onClose は呼び出し側で毎レンダー作り直されることが多い。購読を張り直さず最新を参照する。
  const onCloseRef = useRef(onClose)
  onCloseRef.current = onClose

  useEffect(() => {
    if (!isFrameEmbed || !isOpen) return
    let handled = false
    return subscribeFrameFullscreenExit(() => {
      if (handled) return
      handled = true
      onCloseRef.current()
    })
  }, [isFrameEmbed, isOpen])
}
